import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { z } from "zod";
import { useState, useEffect } from "react";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import Paginat from "@/components/lib/paginat";
import SingleStaffDetailsTable from "@/components/backoffice/all-staff/staff-report/single-staff-table";
import { Button } from "@/components/ui/button";
import { useParams } from "react-router-dom";
import { fetchSingleStaffAllAttendanceReport } from "@/redux/slice/staff-management/staff-attendance";
import { toast } from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { useSearch } from "@/hooks/useSearch";
import useAuth from "@/hooks/useAuth";
import MonthYearSelector from "@/components/common/month-year-selector";
import StaffTableSkeleton from "@/components/skeleton/Staff-skeletion";

const schema = z.object({
  date: z.string().optional(),
});

function SingleStaffDetails() {
  const [monthDate, setMonthDate] = useState(null);
  const { id } = useParams();
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const { user } = useAuth();
  const [firstLoading, setFirstLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  const [searchText, setSearchText] = useState("");

  const {
    singleStaffLoading,
    singleStaffPagination,
    singleStaffattendanceStatistics,
    singleStaffAttendance,
  } = useSelector((state) => state.staffManage.attendance);

  useEffect(() => {
    const fetchData = async () => {
      if (firstLoading) {
        setFirstLoading(true);
      } else {
        setTableLoading(true);
      }
      const res = await dispatch(
        fetchSingleStaffAllAttendanceReport({
          staff_id: id,
          date: monthDate,
          page: page,
          limit: perPage,
        })
      );
      if (fetchSingleStaffAllAttendanceReport.fulfilled.match(res)) {
        setFirstLoading(false);
        setTableLoading(false);
      } else if (fetchSingleStaffAllAttendanceReport.rejected.match(res)) {
        setFirstLoading(false);
        setTableLoading(false);
        toast.error(res.payload);
      }
    };
    fetchData();
  }, [id, dispatch, page, perPage, monthDate]);

  const searchData = useSearch(
    singleStaffAttendance,
    searchText,
    ["name", "designation"],
    300
  );

  if (firstLoading) return <StaffTableSkeleton links={backofficeLinks} />;

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{ icon: true, title: "Staff ID: BY-85236" }}
      paths={[
        { name: "Back Office", path: "/backoffice/overview" },
        {
          name: "Staff Management",
          path: "/backoffice/staff-management/staff/all",
        },
        {
          name: "Staff Report",
          path: "/backoffice/staff-management/staff-reports",
        },
        {
          name: "Single Staff Details",
          path: "/backoffice/staff-management/staff-reports",
        },
      ]}
    >
      <div className="container flex flex-col gap-4 mt-5">
        <h2 className="text-cwhite text-lg font-medium py-3">February 2025</h2>

        <div>
          <div className="w-full flex justify-between lg:flex-row flex-col gap-5 md:gap-20 items-start lg:items-center">
            <div className=" w-full grid grid-cols-3 gap-5 md:grid-cols-5 ">
              <div className="bg-cblack3 py-2 px-4 flex gap-5 rounded-md border border-gray-700">
                <div className="space-y-1 text-white">
                  <h2>Present</h2>
                  <h2 className="text-lg font-bold text-cyellow">
                    {singleStaffattendanceStatistics?.present_days || "0"} Days
                  </h2>
                </div>
              </div>
              <div className="bg-cblack3 py-2 px-4 flex gap-5 rounded-md border border-gray-700">
                <div className="space-y-1 text-white">
                  <h2 className="text-sm xl:text-base">Total Production H</h2>
                  <h2 className="text-lg font-bold text-cred">
                    {singleStaffattendanceStatistics?.total_hours || "0"} H
                  </h2>
                </div>
              </div>
              <div className="bg-cblack3 py-2 px-4 flex gap-5 rounded-md border border-gray-700">
                <div className="space-y-1 text-white">
                  <h2 className="text-sm lg:text-base">Total Over Time</h2>
                  <h2 className="text-lg font-bold text-cred">
                    {singleStaffattendanceStatistics?.overtime || "0"} H
                  </h2>
                </div>
              </div>
              <div className="bg-cblack3 py-2 px-4 flex gap-5 rounded-md border border-gray-700">
                <div className="space-y-1 text-white">
                  <h2 className="text-sm lg:text-base">Absent</h2>
                  <h2 className="text-lg font-bold text-cred">
                    {singleStaffattendanceStatistics?.absent_days || "0"} Days
                  </h2>
                </div>
              </div>
              <div className="bg-cblack3 py-2 px-4 flex gap-5 rounded-md border border-gray-700">
                <div className="space-y-1 text-white">
                  <h2 className="text-sm lg:text-base">Late</h2>
                  <h2 className="text-lg font-bold text-cred">
                    {singleStaffattendanceStatistics?.late || "0"} Days
                  </h2>
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-5 justify-between items-end mt-5">
            <div className="flex items-end gap-5">
              <MonthYearSelector
                defaultValue={monthDate}
                onChange={(val) => setMonthDate(val)}
              />
              <div>
                <Button>Print</Button>
              </div>
            </div>
            <div className="flex gap-3 justify-center items-center">
              <div className="relative">
                <Search
                  size={18}
                  className="text-cwhite3 absolute top-2 left-2"
                />
                <Input
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search by Name or ID"
                  className="bg-cblack3 pl-8"
                />
              </div>
              <div className="flex gap-1 justify-center items-center">
                <h3 className="text-white">Show</h3>
                <Select
                  onValueChange={(value) => setPerPage(Number(value))}
                  value={perPage.toString()}
                >
                  <SelectTrigger className="w-[60px]">
                    <SelectValue placeholder="10" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="40">40</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="mt-5">
            <SingleStaffDetailsTable data={searchData} />
          </div>
          <div>
            <Paginat
              pagination={singleStaffPagination}
              onPageChange={(page) => setPage(page)}
              className="mt-4"
            />
          </div>
        </div>
      </div>
    </Layout2>
  );
}

export default SingleStaffDetails;
